import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { getMissions } from '../../redux/missions/missionActions'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import DashboardMissionsTab from './components/DashboardMissionsTab'
import DashboardMappingControls from './components/DashboardMappingControls'
import './mission.css'
import { TabPanel } from './components/TabPanel'
import { ActionsBoard } from '../../components/containers/ActionsBoard'
import { getGlobalMaturityRate } from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import { selectGlobalMaturityRate } from '../../redux/securityCheckFamily/securityCheckFamilySelector'
import { fetchMyActions } from '../../redux/actorAction/actorActionAction'
import { formatDecimal } from '../../utils/formatNumbers'

const LEVEL_KEYS = [
  'initial',
  'repeatable',
  'defined',
  'managed',
  'optimised',
] as const

function resolveMaturityLevel(raw: number | undefined | null): {
  score: number
  level: number
} {
  if (raw == null || Number.isNaN(Number(raw))) {
    return { score: 1, level: 1 }
  }
  const n = Number(raw)
  // API may return 0–5 scale or a 0–100 percentage
  const score = n > 5 ? Math.min(5, Math.max(0, n / 20)) : Math.min(5, Math.max(0, n))
  const level = Math.max(1, Math.min(5, Math.round(score) || 1))
  return { score: Math.round(score * 10) / 10, level }
}

export default function DisabledTabs() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [value, setValue] = useState(0)
  const dispatch: AppDispatch = useAppDispatch()
  const [selectedMissionId, setSelectedMissionId] = useState<string | undefined>()
  const [mappingCount, setMappingCount] = useState<number | null>(null)

  const [username, setUsername] = useState<string | null>(null)

  const missionsList = useSelector(
    (state: RootState) => state.mission.MissionResponse
  )
  const missions = missionsList?.missions ?? []
  const myActions = useSelector((state: RootState) => state.acteurAction.actions)
  const globalMaturity = useSelector(selectGlobalMaturityRate)

  useEffect(() => {
    dispatch(
      getMissions({
        search: '',
        page: 1,
        pageSize: 50,
      })
    )
    dispatch(getGlobalMaturityRate())
    dispatch(fetchMyActions())
    const storedUsername = localStorage.getItem('username')
    if (storedUsername) {
      setUsername(storedUsername)
    }
  }, [dispatch])

  useEffect(() => {
    if (!missions.length) {
      setSelectedMissionId(undefined)
      return
    }

    if (!selectedMissionId || !missions.some((m) => m._id === selectedMissionId)) {
      setSelectedMissionId(missions[0]?._id)
    }
  }, [missions, selectedMissionId])

  const selectedMission = missions.find((mission) => mission._id === selectedMissionId)

  const taskCount = useMemo(() => {
    const approve = myActions?.Approbateur?.length ?? 0
    const assigned = myActions?.Responsable?.length ?? 0
    return approve + assigned
  }, [myActions])

  const { score: maturityScore, level: maturityLevel } = resolveMaturityLevel(
    globalMaturity?.globalMaturityRate
  )

  const levelLabel = t(`homePage.levels.${LEVEL_KEYS[maturityLevel - 1]}`, {
    defaultValue: LEVEL_KEYS[maturityLevel - 1],
  })

  const dateLabel = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(i18n.language === 'fr' ? 'fr-FR' : 'en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(new Date())
    } catch {
      return new Date().toLocaleDateString()
    }
  }, [i18n.language])

  const programmeName =
    selectedMission?.MissionName ||
    t('dashboard', { defaultValue: 'Dashboard' })

  const displayName = username || '—'

  const subtitle = username
    ? t('homePage.subtitle', {
        name: displayName,
        missions: missions.length,
        tasks: taskCount,
        mappingHint: t('homePage.mappingHint', { defaultValue: '' }),
      })
    : t('homePage.subtitleShort', { name: displayName })

  const tabs = [
    {
      label: t('homePage.tabs.missions'),
      count: missions.length,
      tourId: 'dashboard-tab-missions',
    },
    {
      label: t('homePage.tabs.myTasks'),
      count: taskCount,
      tourId: 'dashboard-tab-tasks',
    },
    {
      label: t('homePage.tabs.securityControlManagement'),
      count: mappingCount,
      tourId: 'dashboard-tab-mapping',
    },
  ]

  return (
    <div className="mission-page dash-page">
      <div className="dash-page-head">
        <div>
          <div className="dash-eyebrow">
            {t('homePage.eyebrow', {
              date: dateLabel,
              programme: programmeName,
            })}
          </div>
          <h1 className="dash-title">{t('homePage.title')}</h1>
          <p className="dash-sub">{subtitle}</p>
        </div>
        <div className="dash-head-actions">
          <button
            type="button"
            className="dash-btn dash-btn-ghost"
            onClick={() => navigate('/maturity/view-analysis/')}
          >
            {t('homePage.maturityAnalysis')}
          </button>
          <button
            type="button"
            className="dash-btn dash-btn-primary"
            onClick={() => navigate('/securityFramework')}
          >
            {t('homePage.openMapping')}
          </button>
        </div>
      </div>

      <div className="dash-posture">
        <div className="dash-posture-grid">
          <div>
            <div className="dash-posture-eyebrow">{t('homePage.maturityLevel')}</div>
            <div className="dash-reuse">
              <span className="dash-reuse-big">{formatDecimal(maturityScore)}</span>
              <span className="dash-reuse-unit">/ 5</span>
            </div>
            <p className="dash-lede">
              {t('homePage.levelLede', {
                level: maturityLevel,
                label: levelLabel.toLowerCase(),
              })}
            </p>
          </div>
          <div className="dash-rule-v" aria-hidden />
          <div>
            <div className="dash-ladder">
              {LEVEL_KEYS.map((_, i) => (
                <span
                  key={LEVEL_KEYS[i]}
                  className={`dash-rung${i < maturityLevel ? ' on' : ''}`}
                />
              ))}
            </div>
            <div className="dash-ladder-lbl">
              {LEVEL_KEYS.map((key, i) => (
                <span
                  key={key}
                  className={i < maturityLevel ? 'on' : undefined}
                >
                  {t(`homePage.levels.${key}`)}
                </span>
              ))}
            </div>
            <div className="dash-ladder-note">
              <span>{t('homePage.ladderNote')}</span>
              <button
                type="button"
                className="dash-btn dash-btn-ghost dash-btn-sm"
                onClick={() => navigate('/maturity/view-analysis/')}
              >
                {t('homePage.viewAnalysis')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="dash-tabs" role="tablist" data-tour-id="dashboard-tabs">
        {tabs.map((tab, index) => (
          <button
            key={tab.tourId}
            type="button"
            role="tab"
            aria-selected={value === index}
            className={`dash-tab${value === index ? ' on' : ''}`}
            data-tour-id={tab.tourId}
            onClick={() => setValue(index)}
          >
            {tab.label}
            {tab.count != null && <span className="dash-tab-n">{tab.count}</span>}
          </button>
        ))}
      </div>

      <TabPanel value={value} index={0} className="mission-main-tabpanel">
        <DashboardMissionsTab
          missions={missions}
          selectedMissionId={selectedMissionId}
          onSelectMission={setSelectedMissionId}
        />
      </TabPanel>
      <TabPanel value={value} index={1} className="mission-main-tabpanel mission-main-tabpanel--secondary">
        <ActionsBoard />
      </TabPanel>
      <TabPanel
        value={value}
        index={2}
        flush
        className="mission-main-tabpanel mission-main-tabpanel--mapping"
      >
        <DashboardMappingControls onFamilyCountChange={setMappingCount} />
      </TabPanel>
    </div>
  )
}
